import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { iDataProyectosResultadoMap } from '../../interfaces/UmmiaApps/iDataProyectosResultadoMap';
import { useAuthContext } from '../../hooks/Auth';
import { desactivarProyecto, getProyectosByUser, suscribeProyect } from '../../controllers/operaciones';
import { suscribeOnUpdateResult } from '../../services/suscriptionsGraphQL';
import { formatoMarkdown } from '../../utils/formatoMarkdown';
import { generarPdf } from '../../utils/generarPdf';
import { generarPdfImprimir } from '../../utils/generarPdfImprimir';
import { toast } from 'react-toastify';


interface ProjectsContextProps {
  projects: iDataProyectosResultadoMap[];
  isLoading: boolean;
  loadProjects: () => Promise<void>;
  assignToDelete: (planificacionId: string) => void;
  toDelete: string;
  isDeleting: boolean;
  subscribeToNewResults: (proyectoId: string) => Promise<void>;
  handleDescargarProyecto: (propuesta: iDataProyectosResultadoMap["propuesta"]) => Promise<void>;  
  handleDelete: (proyectoId: string) => void;
   actualizarPropuestaProyecto: (
    proyectoId: string,
    nuevaPropuesta: iDataProyectosResultadoMap["propuesta"]
  ) => void;
}



const ProjectsContext = createContext<ProjectsContextProps | undefined>(undefined);

export const ProjectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthContext();  
  const [projects, setProjects] = useState<iDataProyectosResultadoMap[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [toDelete, setToDelete] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);

  const loadProjectsFromAPI = async () => {
    if (user) {
      const response = await getProyectosByUser(user.id);
      setProjects(response);
      //localStorage.setItem('misProyectos', JSON.stringify(response));
    }
    setIsLoading(false);
  };

  const handleDescargarProyecto = async (
    propuesta: iDataProyectosResultadoMap["propuesta"]
    
  ) => {
    console.log("handleDescargarProyecto propuesta ->", propuesta);
    return new Promise<void>((resolve) => {
      // Si viene como string, intentamos parsear; si falla, asumimos que ya es Markdown
      if (typeof propuesta === "string") {
        try {
          const parsed = JSON.parse(propuesta as string);
          const formato = formatoMarkdown(parsed);
          generarPdfImprimir(formato);
          resolve();
          return;
        } catch {
          // No es JSON, probablemente ya es Markdown puro
          generarPdfImprimir(propuesta as string);
          resolve();
          return;
        }
      }
//Si viene como objeto y tiene el resultado, se usa
      const anyProp = propuesta as any;
    if (anyProp && typeof anyProp.resultado === "string") {
      // aquí ya guardaste el markdown editado
      generarPdfImprimir(anyProp.resultado);
      resolve();
      return;
    }

      // Si viene como objeto (lo habitual cuando es resultado de la API)
      const formato = formatoMarkdown(propuesta);
      generarPdfImprimir(formato);
      resolve();
    });
  };

  const handleDelete = async () => {
    if(!toDelete || toDelete === "") {
      console.error("No se ha especificado un ID de planificación para eliminar.");
      return;
    }
    console.log({ toDelete });
    setIsDeleting(true);
    console.log("Desactivar proyecto con ID:", toDelete);
    await desactivarProyecto(toDelete);
    // Actualiza el estado local para reflejar la eliminación
    setProjects((prevProjects) => prevProjects.filter((p) => p.proyectoId !== toDelete));
    toast.success("Planificación eliminada exitosamente.");
    setToDelete("");
    setIsDeleting(false);
  };

  const loadProjects = async () => {
      await loadProjectsFromAPI();    
  };


  const assignToDelete = (planificacionId: string) => {
    console.log("Asignar a eliminar:", planificacionId);
    setToDelete(planificacionId);
  };

  const handleUpdateProjects = async (updatedProjectsFromSub: iDataProyectosResultadoMap[]) => {
    setProjects((prevProjects) => {
      const projectsList = [...prevProjects]; // Crea una copia del estado actual
      const updatedList = addNewProjects(projectsList, updatedProjectsFromSub);
      return updatedList;
    });
  };

  const addNewProjects = (projectsList: iDataProyectosResultadoMap[], updatedProjectsFromSub: iDataProyectosResultadoMap[]) => {
    updatedProjectsFromSub.forEach((newProj: any) => {
      // Agrega el proyecto solo si no existe ya en el array
      if (!projectsList.some((p: any) => p.proyectoId === newProj.proyectoId)) {
        projectsList.push(newProj);
      }
    });
    return projectsList;
  };

  const subscribeToNewProjects = async () => {
    if (user) {
      const updatedProjectsFromSub = await suscribeProyect(user.id);      
      if (updatedProjectsFromSub && updatedProjectsFromSub.length > 0) {
        handleUpdateProjects(updatedProjectsFromSub);
      }
    }
  };
  

  const subscribeToNewResults = async (proyectoId: string) => {
    const newResult = await suscribeOnUpdateResult(proyectoId);
    if (newResult) {      
      let projectsList = [...projects];
      const updatedList = projectsList.map((proyecto: iDataProyectosResultadoMap) => {
        if (proyecto.proyectoId === proyectoId) {
          const { resultado } = newResult;
          const objetoFinalResultado = JSON.parse(resultado);
          return {
            ...proyecto,
            calificacion: newResult.calificacion ?? 0,
            propuesta: objetoFinalResultado.propuestas || resultado,
          };
        }
        return proyecto;
      });
      setProjects(updatedList);      
    }
  };

  const actualizarPropuestaProyecto = (
    proyectoId: string,
    nuevaPropuesta: iDataProyectosResultadoMap["propuesta"]
  ) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.proyectoId === proyectoId
          ? { ...p, propuesta: nuevaPropuesta }
          : p
      )
    );
  };


  useEffect(() => {
    loadProjects();
    subscribeToNewProjects();      
  }, [user]);

  const contextValue = useMemo(() => ({
    projects,
    isLoading,
    loadProjects,
    subscribeToNewResults,
    handleDescargarProyecto,
    assignToDelete,
    isDeleting,
    toDelete,
    handleDelete,
    actualizarPropuestaProyecto,
  }), [projects, isLoading, loadProjects, subscribeToNewResults, handleDescargarProyecto]);

  return (
    <ProjectsContext.Provider value={contextValue}>
      {children}
    </ProjectsContext.Provider>
  );
};

export const useProjectsContext = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjectsContext debe ser usado con ProjectsProvider');
  }
  return context;
};
